import React from "react";
import { Box, Chip, Typography, Badge } from "@mui/material";
import { Dashboard as DashboardIcon } from "@mui/icons-material";
import SupabaseImageAvatar from "../utils/components/SupabaseImageAvatar";
import { getMm } from "../theme/modernMaterialEnterprise";
import {
  getFocusVisibleSx,
  getInteractiveCardSx,
  getMotionTransition,
} from "../theme/landingDashboardStyles";

function isChildPendingApproval(user, parentAnchorID) {
  if (!user || user.isParent || parentAnchorID == null) return false;
  const isParent1 =
    user.parentID === parentAnchorID || String(user.parentID) === String(parentAnchorID);
  const isParent2 =
    user.parentID2 === parentAnchorID || String(user.parentID2) === String(parentAnchorID);
  if (isParent1 && user.parentIDApproved !== true) return true;
  if (isParent2 && user.parentID2Approved !== true) return true;
  return false;
}

function FamilyAccountChip({
  account,
  isActive,
  isStaffOverview,
  isPendingApproval,
  unreadCount,
  imageRefreshKey,
  isDarkMode,
  t,
  onSelect,
  fullWidth = false,
}) {
  const disabled = isPendingApproval;
  const label = isStaffOverview
    ? t("landing_family_staff_overview")
    : account.name || t("no_name_available");

  return (
    <Box
      component="button"
      type="button"
      disabled={disabled}
      onClick={() => {
        if (!disabled && !isActive) onSelect(account);
      }}
      sx={{
        width: fullWidth ? "100%" : "auto",
        flex: fullWidth ? "1 1 auto" : "0 0 auto",
        display: "flex",
        alignItems: "center",
        gap: 1,
        minWidth: fullWidth ? 0 : { xs: 148, sm: 168 },
        maxWidth: fullWidth ? "100%" : { xs: 200, sm: 220 },
        px: 1.25,
        py: 1,
        borderRadius: getMm(isDarkMode).radiusSm,
        ...getInteractiveCardSx(isDarkMode, { clickable: !disabled && !isActive }),
        border: isActive
          ? `2px solid ${getMm(isDarkMode).secondary}`
          : `1px solid ${getMm(isDarkMode).outlineVariant}`,
        ...(isActive
          ? {
              backgroundColor: isDarkMode
                ? "rgba(132, 212, 211, 0.12)"
                : "rgba(0, 106, 106, 0.08)",
            }
          : {}),
        cursor: disabled ? "not-allowed" : isActive ? "default" : "pointer",
        opacity: disabled ? 0.65 : 1,
        textAlign: "left",
        fontFamily: getMm(isDarkMode).fontBody,
        ...getMotionTransition(),
        ...getFocusVisibleSx(isDarkMode),
      }}
    >
      <Badge
        badgeContent={unreadCount > 0 ? unreadCount : 0}
        color="error"
        overlap="circular"
        invisible={!unreadCount || unreadCount <= 0}
        sx={{ "& .MuiBadge-badge": { fontSize: "0.625rem", minWidth: 16, height: 16 } }}
      >
        <SupabaseImageAvatar
          user={account}
          size={40}
          refreshKey={imageRefreshKey}
          sx={{
            border: isDarkMode ? "2px solid #4B5563" : "2px solid #E5E7EB",
            flexShrink: 0,
          }}
        />
      </Badge>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          noWrap
          sx={{
            fontWeight: 700,
            fontSize: "0.8125rem",
            color: getMm(isDarkMode).onSurface,
            lineHeight: 1.25,
          }}
        >
          {label}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.25, flexWrap: "wrap" }}>
          {isStaffOverview && (
            <Chip
              icon={<DashboardIcon sx={{ fontSize: "0.875rem !important" }} />}
              label={t("landing_family_you")}
              size="small"
              sx={{
                height: 20,
                fontSize: "0.625rem",
                fontWeight: 600,
                "& .MuiChip-icon": { ml: 0.5 },
              }}
            />
          )}
          {!isStaffOverview && account.isParent && (
            <Chip
              label={t("parent")}
              size="small"
              sx={{ height: 20, fontSize: "0.625rem", fontWeight: 600 }}
            />
          )}
          {isPendingApproval && (
            <Chip
              label={t("pending_approval")}
              size="small"
              color="warning"
              sx={{ height: 20, fontSize: "0.625rem", fontWeight: 600 }}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default function LandingFamilySwitcher({
  accounts,
  activeUser,
  loggedInAccount,
  showStaffOverviewForLoggedIn,
  parentAnchorID,
  unreadCountsByStudent,
  imageRefreshKey,
  isDarkMode,
  t,
  onSwitch,
  isMobile = false,
}) {
  if (!accounts || accounts.length <= 1) return null;

  return (
    <Box sx={{ mb: { xs: 2, sm: 2.5 }, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <Typography
        sx={{
          fontWeight: 500,
          color: getMm(isDarkMode).onSurface,
          mb: 0.5,
          fontSize: { xs: "1rem", sm: "1.125rem" },
          fontFamily: getMm(isDarkMode).fontDisplay,
          letterSpacing: "-0.01em",
        }}
      >
        {t("landing_family_switch_title")}
      </Typography>
      <Typography
        sx={{
          color: getMm(isDarkMode).onSurfaceVariant,
          fontWeight: 500,
          fontSize: { xs: "0.8125rem", sm: "0.875rem" },
          mb: 1.25,
        }}
      >
        {t("landing_family_switch_desc")}
      </Typography>
      <Box
        sx={{
          display: isMobile ? "grid" : "flex",
          gridTemplateColumns: isMobile ? "1fr" : undefined,
          flexWrap: isMobile ? undefined : "wrap",
          gap: 1,
          width: "100%",
          minWidth: 0,
          overflow: "hidden",
          ...(isMobile
            ? {}
            : {
                overflowX: "auto",
                pb: 0.5,
                scrollbarWidth: "thin",
                scrollbarColor: isDarkMode ? "#4B5563 transparent" : "#D1D5DB transparent",
                "&::-webkit-scrollbar": { height: 6 },
                "&::-webkit-scrollbar-thumb": {
                  borderRadius: 999,
                  backgroundColor: getMm(isDarkMode).outline,
                },
              }),
        }}
      >
        {accounts.map((account) => {
          const isActive = activeUser?.studentID === account.studentID;
          const isLoggedInAccount =
            loggedInAccount && account.studentID === loggedInAccount.studentID;
          const isStaffOverview = showStaffOverviewForLoggedIn && isLoggedInAccount;
          const isPendingApproval = isChildPendingApproval(account, parentAnchorID);

          return (
            <FamilyAccountChip
              key={account.studentID}
              account={account}
              isActive={isActive}
              isStaffOverview={isStaffOverview}
              isPendingApproval={isPendingApproval}
              unreadCount={unreadCountsByStudent[account.studentID] || 0}
              imageRefreshKey={imageRefreshKey}
              isDarkMode={isDarkMode}
              t={t}
              onSelect={onSwitch}
              fullWidth={isMobile}
            />
          );
        })}
      </Box>
    </Box>
  );
}
